<template>
  <div :class="`loading-screen ${loadedRef ? 'loading-screen--disabled' : ''}`">
    <LoadingIcon color="primary" />
    <p v-if="isI18nLoaded()">{{ isAuthRef ? t('auth.loadingGuard.login') : t('ui.message.loading') }}</p>
    <div class="bottom-info">
      <p v-if="isI18nLoaded()">
        {{ t('auth.loadingGuard.help.title') }}
        <br />
        <a href="#brevoConversationsExpanded">{{ t('auth.loadingGuard.help.body') }}</a>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'

import useIsAuth from '@/features/auth/hooks/use-is-auth.hook'
import usePrograms from '@/features/programs/hooks/use-programs.hook'
import LoadingIcon from '@/features/ui/components/icons/loading-icon.vue'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import useRouter from '@/hooks/use-router.hook'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { useI18n } from '@/lib/i18n'
import { ROUTES } from '@/utils/config/constants'

const { isAuthRef, isAuthLoadingRef } = useIsAuth()
const { whiteLabelRef, isPendingRef: isWhiteLabelPendingRef, isErrorRef } = useWhiteLabel()
const { isPendingRef: isProgramsPendingRef } = usePrograms()
const { programRolesRef } = useProgramRoles()
const router = useRouter()

const { t, messages, locale } = useI18n()

/**
 * Check if the i18n is loaded
 */
function isI18nLoaded() {
  return Object.keys(messages.value[locale.value] ?? {}).length > 0
}

const loadedRef = ref(false)

// all conditions to pass to remove the loading screen
const conditionsRef = computed(() => ({
  onLogoutRoute: router?.currentRoute.path === ROUTES.LOGOUT, // if the user is on the logout page
  onAuthRoute: router?.currentRoute.path?.startsWith('/auth') || false, // if the user is on the auth page
  programRolesLoaded: !isAuthRef.value || !!programRolesRef.value?.length, // if user is logged off or if the program roles are loaded (make sure that if user is logged in, the program roles are loaded)
  whiteLabelDataLoaded: !isErrorRef.value && !!whiteLabelRef.value && !isWhiteLabelPendingRef.value, // if the white label data is loaded
  programsLoaded: !isAuthRef.value || !isProgramsPendingRef.value, // if user is logged off or if the programs are loaded (make sure that if user is logged in, the programs are loaded)
}))

watchEffect((onCleanUp) => {
  let loadingTimeout: any

  // general condition to check if the app is correctly loaded
  if (
    !isAuthLoadingRef.value &&
    conditionsRef.value.whiteLabelDataLoaded &&
    !conditionsRef.value.onLogoutRoute &&
    conditionsRef.value.programsLoaded &&
    conditionsRef.value.programRolesLoaded
  ) {
    loadingTimeout = setTimeout(() => {
      loadedRef.value = true
    }, 500)
  }

  onCleanUp(() => {
    clearTimeout(loadingTimeout)
  })
})
</script>

<style lang="scss">
@import './loading-guard.scss';
</style>
